import React, { useEffect, useState } from "react";
import { FaRupeeSign } from "react-icons/fa";
import { toast } from "react-toastify";
import styles from "./AddCategoriesPanel.module.css";
import CrossButton from "../../Customs/Button/CrossButton";
import {
  ProductCategoryOption,
  createWoodQuality,
  fetchProductCategories,
} from "../../Services/woodQualities";

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
}

const isNumeric = (v: string) => v === "" || /^[0-9]+(\.[0-9]+)?$/.test(v);

const AddWoodQualityPanel: React.FC<Props> = ({ onClose, onSuccess }) => {
  const [name, setName] = useState("");
  const [categories, setCategories] = useState<ProductCategoryOption[]>([]);
  const [prices, setPrices] = useState<{ [id: string]: string }>({});
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const list = await fetchProductCategories();
        setCategories(list);
      } catch {
        toast.error("Failed to load product categories");
      } finally {
        setLoadingCategories(false);
      }
    })();
  }, []);

  const validate = () => {
    const next: { [k: string]: string } = {};
    if (!name.trim()) next.name = "Wood quality name is required.";
    else if (name.trim().length > 100) next.name = "Maximum 100 characters allowed.";
    categories.forEach((c) => {
      const val = prices[String(c.id)];
      if (val === "" || val === undefined) {
        next[`cat_${c.id}`] = `Price for ${c.name} is required.`;
      } else if (!isNumeric(val) || Number(val) < 0) {
        next[`cat_${c.id}`] = "Only zero or positive numbers allowed.";
      }
    });
    return next;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    setLoading(true);
    try {
      await createWoodQuality({
        name: name.trim(),
        category_prices: categories.map((c) => ({
          category_id: c.id,
          price: Number(prices[String(c.id)]),
        })),
      });
      toast.success("Wood quality added");
      onSuccess?.();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to add wood quality");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.panel}>
        <CrossButton onClick={onClose} />
        <h2 className={styles.heading}>Add Wood Quality</h2>
        <form className={styles.panelBody} onSubmit={handleSubmit} autoComplete="off">
          <label className={styles.label}>Wood Quality Name *</label>
          <input
            className={`${styles.input} ${errors.name ? styles.inputError : ""}`}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setErrors((prev) => ({ ...prev, name: "" }));
            }}
            maxLength={100}
            placeholder="e.g. Hardwood"
          />
          {errors.name && <div className={styles.error}>{errors.name}</div>}

          <div className={styles.sectionTitle}>
            <FaRupeeSign color="#12b76a" style={{ marginRight: 6 }} />
            Price per Product Category
          </div>
          <div className={styles.sectionSubtitle}>
            Enter the stored price for this wood quality under every existing product category.
            Cart pricing uses category + wood quality on the backend.
          </div>

          {loadingCategories ? (
            <div className={styles.muted}>Loading product categories…</div>
          ) : categories.length === 0 ? (
            <div className={styles.muted}>No product categories found. The quality will be added without prices.</div>
          ) : (
            <div className={styles.sectionGroup2col}>
              {categories.map((c) => (
                <div key={c.id}>
                  <label className={styles.label}>{c.name} *</label>
                  <input
                    className={`${styles.input} ${errors[`cat_${c.id}`] ? styles.inputError : ""}`}
                    type="number"
                    min="0"
                    step="any"
                    placeholder="Price including Tax."
                    value={prices[String(c.id)] ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || (/^[0-9]+(\.[0-9]{0,2})?$/.test(value) && Number(value) >= 0)) {
                        setPrices((prev) => ({ ...prev, [String(c.id)]: value }));
                        setErrors((prev) => ({ ...prev, [`cat_${c.id}`]: "" }));
                      }
                    }}
                  />
                  {errors[`cat_${c.id}`] && <div className={styles.error}>{errors[`cat_${c.id}`]}</div>}
                </div>
              ))}
            </div>
          )}

          <div className={styles.buttonRow}>
            <button type="submit" className={styles.addBtn} disabled={loading || loadingCategories}>
              {loading ? "Saving..." : "Add Wood Quality"}
            </button>
            <button type="button" className={styles.closeBtn} onClick={onClose} disabled={loading}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddWoodQualityPanel;
